import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Trophy } from 'lucide-react';
import { Button, MoneyText } from '@/ui';
import {
  OFFER_PRICE_FRACTION_DIGITS,
  formatMoney,
  totalAmount,
  totalOf,
  unitRate,
} from '@/lib/money';
import { serverNow } from '@/lib/clock-skew';
import { pairOf } from '@/lib/task-currency';
import { isActiveAcceptedStatus } from '@/lib/task-status';
import type { TaskRow } from '../types';
import { PaymentDueNotice } from './PaymentDue';
import { QuoteActions, QuoteCard, QuoteHeader } from './QuoteCard';
import { useCountdown } from './useCountdown';
import styles from './CustomerRequestCard.module.css';

export type CustomerRequestCardProps = {
  task: TaskRow;
  receivedAtMs: number;
  /** Tamamlanan isleme itiraz suresi (sn) — /v1/config. */
  disputeSeconds: number;
  /** false ise kart SALT OKUNUR: baskasinin talebi (musteri org yoneticisi gorunumu). */
  isOwner: boolean;
  /**
   * Talebi ACAN kisinin adi — YALNIZ musteri org yoneticisi baskasinin talebine bakarken.
   * Kaldirilan "Gelen teklifler" ekraninin tek ozgun isiydi (2026-10-02); kendi talebinde
   * her karta kendi adini yazmak gurultu olurdu, o yuzden cagiran tarafta kosullu.
   */
  ownerName?: string | undefined;
  /** Talep detayi (/musteri/talepler/:taskId) — teklifler, kazanan ve odeme bilgileri orada. */
  onOpen: () => void;
  onCancel: () => void;
  onWarn: () => void;
};

/**
 * MUSTERININ TALEP KARTI (Taleplerim).
 *
 * Hangi dugmenin gorunecegini FAZ + DURUM belirler. Kurallar SUNUCUNUN gecis tablosunun
 * aynasi (task-status.js, 2026-09-23); dugme gorunup istek reddedilmesin diye:
 *   - iptal       : yalniz TEKLIF fazinda ('new')                     -> CANCEL_WINDOW_CLOSED
 *   - detay       : acik talepte TEKLIF fazindan itibaren "Teklifleri gor" (2026-10-02: teklifler
 *                   teklif fazinda da canli gorunur, SECIM karar fazinda acilir); karar
 *                   verildiyse "Talep detayi" (kazanan + odeme bilgileri ayni sayfada).
 *   - uyari       : (a) 'pending' / 'payment_received' ve odeme / gonderim penceresi DOLMUS
 *                       (faz passive)                                     -> WARNING_TOO_EARLY
 *                   (b) 'completed' ve itiraz suresi icinde (statusUpdatedAt = tamamlanma ani)
 *                                                                       -> DISPUTE_WINDOW_CLOSED
 *                   warning / cancelled / expired / no_offers / resolved / refunded: YOK.
 *
 * ================================ OZET ======================================
 * Faza gore TEK satir ve YENI BIR SATIR ACMADAN: eylem satirinin solunda (dugmeler saga
 * yaslaniyor). Kaynak sunucunun talep ozeti (TaskRow.offerCount ...); alanlar null ise
 * (sahibi olmayan cagiran) ozet hic cizilmez — "0 teklif" yazmak yanlis olurdu.
 *   acik talep : "3 teklif · en iyi 49,05 TRY" ya da "Henuz teklif yok"
 *   karar var  : "Kazanan: <buro> · 34,25 TRY · Toplam: 34.250,00 TRY" (Islemde ve Gecmis)
 *
 * Odeme uyarisi (2026-09-26): 'pending' ve odeme penceresi ACIKKEN sahibine kirmizi bir not +
 * "Odeme bilgileri" (talep detayi; kazanan teklifte buronun IBAN'i var). Yalniz SAHIBINDE:
 * metin "onayladiginiz teklif" diyor; yonetici baskasinin odemesini yapamaz.
 */
export function CustomerRequestCard({
  task,
  receivedAtMs,
  disputeSeconds,
  isOwner,
  ownerName,
  onOpen,
  onCancel,
  onWarn,
}: CustomerRequestCardProps) {
  const { t } = useTranslation();
  const phase = useCountdown({
    taskStatusCode: task.taskStatusCode,
    countdownStatus: task.countdownStatus,
    statusUpdatedAt: task.statusUpdatedAt,
    countdown: task.countdown,
    receivedAtMs,
  });

  const pair = pairOf(task.taskTypeCode);
  const decided = task.pretenderUserId !== null;
  const open = task.taskStatusCode === 'new' && (phase.phase === 'offer' || phase.phase === 'decide');

  // Eylemler YALNIZ sahibinde. Detayi gormek herkese acik: yonetici org'un isini izleyebilmeli,
  // ama kazanani secen ekran zaten sahiplik kontrolu yapiyor.
  const canCancel = isOwner && phase.phase === 'offer' && task.taskStatusCode === 'new';
  const canOpen = decided || open;
  const canWarn = isOwner && warnWindowOpen(task, phase.phase, disputeSeconds);
  const paymentDue = isOwner && task.taskStatusCode === 'pending' && phase.phase === 'payment';

  const facts: ReactNode[] = [];
  if (ownerName !== undefined) {
    facts.push(
      <span key="owner" className={styles.fact}>
        {`${t('task.owner')}: ${ownerName}`}
      </span>,
    );
  }
  if (decided) {
    if (task.acceptedBusinessName !== null) {
      facts.push(
        <span key="winner" className={[styles.fact, styles.winner].join(' ')}>
          <Trophy className={styles.trophy} size={13} strokeWidth={2.2} aria-hidden="true" />
          {t('task.summary.winner', { name: task.acceptedBusinessName })}
        </span>,
      );
    }
    if (task.acceptedOfferPrice !== null) {
      const rate = unitRate(task.acceptedOfferPrice);
      facts.push(
        <span key="rate" className={styles.fact}>
          <MoneyText
            rate={rate}
            // Teklif fiyati numeric(11,2) — piyasa kurunun 4 hanesi burada uydurma olurdu.
            fractionDigits={OFFER_PRICE_FRACTION_DIGITS}
            currency={pair.secondCurrency}
            size="caption"
          />
        </span>,
        <span key="total" className={styles.fact}>
          {t('labels.total')}
          <MoneyText
            total={totalOf(rate, task.taskAmount)}
            currency={pair.secondCurrency}
            size="caption"
            tone="primary"
          />
        </span>,
      );
    }
  } else if (open && task.offerCount !== null) {
    if (task.offerCount === 0) {
      facts.push(
        <span key="none" className={styles.fact}>
          {t('task.summary.noOffers')}
        </span>,
      );
    } else {
      facts.push(
        <span key="count" className={styles.fact}>
          {t('task.summary.offerCount', { count: task.offerCount })}
        </span>,
      );
      if (task.bestOfferPrice !== null) {
        const best = `${formatMoney(task.bestOfferPrice, OFFER_PRICE_FRACTION_DIGITS)} ${pair.secondCurrency}`;
        facts.push(
          <span key="best" className={[styles.fact, styles.best].join(' ')}>
            {t('task.summary.best', { price: best })}
          </span>,
        );
      }
    }
  }

  return (
    <QuoteCard phase={phase}>
      <QuoteHeader
        id={task.id}
        taskTypeCode={task.taskTypeCode}
        isBuying={task.taskIsBuying}
        amount={totalAmount(task.taskAmount)}
        amountCurrency={pair.currency}
        createdAt={task.createdAt}
        phase={phase}
        warning={task.taskStatusCode === 'warning'}
        statusCode={task.taskStatusCode}
      />

      {/* "Odeme bilgileri" talep detayina gider: buronun IBAN'i kazanan teklifte. */}
      {paymentDue ? <PaymentDueNotice remainingMs={phase.remainingMs} onAction={onOpen} /> : null}

      {facts.length > 0 || canCancel || canWarn || canOpen ? (
        <QuoteActions>
          {facts.length > 0 ? <p className={styles.facts}>{facts}</p> : null}
          {canCancel ? (
            <Button tone="neutral" size="sm" onClick={onCancel}>
              {t('actions.cancel')}
            </Button>
          ) : null}
          {canWarn ? (
            <Button tone="neutral" size="sm" onClick={onWarn}>
              {t('actions.sendWarning')}
            </Button>
          ) : null}
          {canOpen ? (
            <Button size="sm" onClick={onOpen}>
              {decided ? t('task.requestDetail') : t('task.seeOffers')}
            </Button>
          ) : null}
        </QuoteActions>
      ) : null}
    </QuoteCard>
  );
}

/**
 * Uyari penceresi acik mi? (sunucu: db-scripts.js transitionWindow('dispute'))
 *
 * 'pending' ve 'payment_received' (buronun gonderim suresi, 2026-09-26) kolu fazdan okunur:
 * odeme zinciri 'payment' -> 'passive' ve 'passive' tam olarak "pencere doldu" demek.
 * 'completed' kolu faz tasimiyor (kapali satir), sure
 * `statusUpdatedAt`ten SUNUCU SAATIYLE (serverNow) olculur — cihaz saati yanlissa dugme
 * yanlis anda acilip kapanirdi. Kapali satir tiklanmadigi icin dugme sure dolunca
 * kendiliginden kaybolmaz; sunucu o durumda DISPUTE_WINDOW_CLOSED doner ve mesaji gosterilir.
 */
const warnWindowOpen = (task: TaskRow, phase: string, disputeSeconds: number): boolean => {
  if (isActiveAcceptedStatus(task.taskStatusCode)) return phase === 'passive';
  if (task.taskStatusCode === 'completed') {
    const completedAt = task.statusUpdatedAt ? Date.parse(task.statusUpdatedAt) : Number.NaN;
    if (!Number.isFinite(completedAt)) return false;
    return serverNow() - completedAt < disputeSeconds * 1000;
  }
  return false;
};
